import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import PortfolioModal from './PortfolioModal';

const PortfolioSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const projects = [
    {
      title: 'NexGenCX',
      category: 'Web Platform',
      description: 'Next-generation customer experience platform with advanced analytics and automation',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop',
      tags: ['React', 'CX', 'Enterprise', 'Analytics'],
      url: 'https://www.nexgencx.com/'
    },
    {
      title: 'Wellversed',
      category: 'Health Platform',
      description: 'AI-powered wellness platform with personalized health plans and expert guidance',
      image: 'https://health-app-hub.preview.emergentagent.com/assets/hero-video.mp4',
      tags: ['React', 'AI', 'Healthcare', 'Wellness'],
      url: 'https://health-app-hub.preview.emergentagent.com/',
      isVideo: true
    },
    {
      title: 'Pulse Fitness',
      category: 'Fitness App',
      description: 'Personalized fitness journey platform with custom workout tracking and health assessments',
      image: 'https://customer-assets.emergentagent.com/job_fitness-tracker-446/artifacts/itr16q8x_AI_Hit_Collective_extremely_muscular_beautiful_woman_--ar_43_--_db262609-0dc0-4696-be81-7f311dd4d65b%20%281%29.png',
      tags: ['React', 'Fitness', 'Health', 'Tracking'],
      url: 'https://fitness-tracker-446.preview.emergentagent.com/'
    },
    {
      title: "Nature's Hype",
      category: 'E-Commerce Platform',
      description: 'Cold-pressed juice brand with gut cleanse programs, immunity boosters, and natural energy drinks',
      image: 'https://vibejuice.preview.emergentagent.com/static/media/hero-video.mp4',
      tags: ['E-Commerce', 'Health', 'React', 'Branding'],
      url: 'https://vibejuice.preview.emergentagent.com/',
      isVideo: true
    },
    {
      title: 'CyberShield',
      category: 'Cybersecurity Platform',
      description: 'Vendor-neutral security advisory platform serving mid-market CISOs with compliance, network transformation, and managed security solutions',
      image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?w=800&h=600&fit=crop',
      tags: ['Security', 'Enterprise', 'Advisory', 'Compliance'],
      url: 'https://cyber-shield-web.preview.emergentagent.com/'
    }
  ];

  return (
    <section id="portfolio" className="relative py-24 overflow-hidden">
      {/* Image Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_creative-studio-146/artifacts/d48yrw0l_sanjuanventures_An_East_african_young_woman_with_long_neatly__496593c3-b8ec-42d0-b59d-5949ebfe4f4a_0.png"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            Our <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Portfolio</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto drop-shadow-md">
            Explore some of our recent projects and success stories.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="group bg-white/5 backdrop-blur-sm rounded-2xl border-2 border-white/10 overflow-hidden hover:border-violet-400/50 transition-all duration-300 hover:shadow-xl"
            >
              <div className="relative h-64 overflow-hidden">
                {project.isVideo ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  >
                    <source src={project.image} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <a href={project.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="secondary" size="sm" className="bg-white/90 hover:bg-white">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Project
                    </Button>
                  </a>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm font-semibold text-pink-400 mb-2 drop-shadow-md">{project.category}</p>
                <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">{project.title}</h3>
                <p className="text-white/90 mb-4 drop-shadow-md">{project.description}</p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-white/20 text-white text-sm rounded-full backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button 
            size="lg"
            onClick={() => setIsModalOpen(true)}
            className="bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold px-8 hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            View All Projects
          </Button>
        </div>
      </div>

      {/* Portfolio Modal */}
      <PortfolioModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projects={projects}
      />
    </section>
  );
};

export default PortfolioSection;